import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable()
export class TimezoneInterceptor implements HttpInterceptor {

  /**
   * Intercepts HTTP requests and adds timezone information as headers
   * @param req - The outgoing HTTP request
   * @param next - The next handler in the interceptor chain
   * @returns Observable of the HTTP event
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Get timezone information
    const timezoneHeaders = this.getTimezoneHeaders();

    // Clone the request and add the timezone headers
    const modifiedRequest = req.clone({
      setHeaders: timezoneHeaders
    });

    // Continue with the modified request
    return next.handle(modifiedRequest);
  }

  /**
   * Get timezone information as headers object
   * @returns Object containing timezone headers
   */
  private getTimezoneHeaders(): { [key: string]: string } {
    try {
      // Get the user's timezone using Intl API
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Get the timezone offset in minutes
      // Note: getTimezoneOffset() returns positive values for UTC- and negative for UTC+
      // We invert it to follow standard convention (positive for UTC+, negative for UTC-)
      const timeZoneOffsetMinutes = -new Date().getTimezoneOffset();

      // Convert offset to hours for easier server-side processing
      const timeZoneOffsetHours = timeZoneOffsetMinutes / 60;

      // Create a formatted timezone offset string (e.g., "+05:00", "-08:00")
      const offsetHours = Math.floor(Math.abs(timeZoneOffsetMinutes) / 60);
      const offsetMinutes = Math.abs(timeZoneOffsetMinutes) % 60;
      const sign = timeZoneOffsetMinutes >= 0 ? '+' : '-';
      const formattedOffset = `${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;

      // Return headers object
      return {
        'X-Client-Timezone': timeZone,
        'X-Client-Timezone-Offset': timeZoneOffsetMinutes.toString(),
        'X-Client-Timezone-Offset-Hours': timeZoneOffsetHours.toString(),
        'X-Client-Timezone-Offset-String': formattedOffset,
        'X-Client-Timestamp': new Date().toISOString()
      };

    } catch (error) {
      console.warn('Failed to get timezone information:', error);

      // Provide fallback values if timezone detection fails
      return {
        'X-Client-Timezone': 'UTC',
        'X-Client-Timezone-Offset': '0',
        'X-Client-Timezone-Offset-Hours': '0',
        'X-Client-Timezone-Offset-String': '00:00',
        'X-Client-Timestamp': new Date().toISOString()
      };
    }
  }
}
